import { getRiskLabel, formatDate } from "./pdfStyles";

const escapeCSV = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  const stringValue = String(value);
  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const generateCSVContent = (headers, rows) => {
  const headerLine = headers.map(escapeCSV).join(",");
  const dataLines = rows.map((row) => row.map(escapeCSV).join(","));
  return [headerLine, ...dataLines].join("\n");
};

const downloadCSV = (content, filename) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

export const generateUserCSV = (userData) => {
  const headers = [
    "Subject ID",
    "Sex",
    "Age",
    "Height (cm)",
    "Weight (kg)",
    "Risk Level",
    "Risk Updated",
    "Recommendation",
    "Status",
    "Recommendation Date",
    "Notes",
  ];

  const rows = [];

  // If user has recommendations, create a row for each
  if (userData.recommendations && userData.recommendations.length > 0) {
    userData.recommendations.forEach((rec) => {
      rows.push([
        userData.subject_id,
        userData.sex,
        userData.age,
        userData.height,
        userData.weight,
        getRiskLabel(userData.latest_risk_level),
        formatDate(userData.risk_updated_at),
        rec.recommendation,
        rec.status?.replace("Status.", "") || "",
        formatDate(rec.created_at),
        rec.notes || "",
      ]);
    });
  } else {
    // Create single row without recommendation data
    rows.push([
      userData.subject_id,
      userData.sex,
      userData.age,
      userData.height,
      userData.weight,
      getRiskLabel(userData.latest_risk_level),
      formatDate(userData.risk_updated_at),
      "",
      "",
      "",
      "",
    ]);
  }

  const content = generateCSVContent(headers, rows);
  const dateStr = new Date().toISOString().split("T")[0];
  const filename = `oncodir_user_${userData.subject_id}_${dateStr}.csv`;

  downloadCSV(content, filename);
  return filename;
};

export const generateBulkCSV = (exportData, includeRecommendations = false) => {
  if (includeRecommendations) {
    return generateBulkCSVWithRecommendations(exportData);
  }
  return generateBulkCSVSummary(exportData);
};

const generateBulkCSVSummary = (exportData) => {
  const headers = [
    "Subject ID",
    "Sex",
    "Age",
    "Height (cm)",
    "Weight (kg)",
    "Risk Level",
    "Risk Updated",
    "Total Recommendations",
    "Pending Recommendations",
    "Completed Recommendations",
  ];

  const rows = exportData.users.map((user) => [
    user.subject_id,
    user.sex,
    user.age,
    user.height,
    user.weight,
    getRiskLabel(user.latest_risk_level),
    formatDate(user.risk_updated_at),
    user.total_recommendations,
    user.pending_recommendations,
    user.completed_recommendations,
  ]);

  const content = generateCSVContent(headers, rows);
  const dateStr = new Date().toISOString().split("T")[0];
  const filename = `oncodir_bulk_export_${dateStr}.csv`;

  downloadCSV(content, filename);
  return filename;
};

const generateBulkCSVWithRecommendations = (exportData) => {
  const headers = [
    "Subject ID",
    "Sex",
    "Age",
    "Height (cm)",
    "Weight (kg)",
    "Risk Level",
    "Risk Updated",
    "Recommendation",
    "Status",
    "Recommendation Date",
    "Notes",
  ];

  const rows = [];

  exportData.users.forEach((user) => {
    if (user.recommendations && user.recommendations.length > 0) {
      user.recommendations.forEach((rec) => {
        rows.push([
          user.subject_id,
          user.sex,
          user.age,
          user.height,
          user.weight,
          getRiskLabel(user.latest_risk_level),
          formatDate(user.risk_updated_at),
          rec.recommendation,
          rec.status?.replace("Status.", "") || "",
          formatDate(rec.created_at),
          rec.notes || "",
        ]);
      });
    } else {
      rows.push([
        user.subject_id,
        user.sex,
        user.age,
        user.height,
        user.weight,
        getRiskLabel(user.latest_risk_level),
        formatDate(user.risk_updated_at),
        "",
        "",
        "",
        "",
      ]);
    }
  });

  const content = generateCSVContent(headers, rows);
  const dateStr = new Date().toISOString().split("T")[0];
  const filename = `oncodir_bulk_export_detailed_${dateStr}.csv`;

  downloadCSV(content, filename);
  return filename;
};

export default { generateUserCSV, generateBulkCSV };
